import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormMultiSelectComponent, FormSelectComponent, FormTextareaComponent } from '@shared/forms';
import { PathwayFacade } from '@app/store/pathway';
import { PathwayType } from '@app/store/shared/models';
import { FormOptionsFacade } from '@app/store/form-options';
import { CourseSelection, PredefinedPathwayCourseSelectionService } from '../select-course-dialog/course-selection.service';
import { CourseListComponent } from '../course-list/course-list.component';
import { getTypeOptions } from '../list/config/type-options';

@Component({
    selector: 'app-predefined-pathway-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormSelectComponent,
        FormMultiSelectComponent,
        FormTextareaComponent,
        CourseListComponent,
    ],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly pathwayFacade = inject(PathwayFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly courseSelectionService = inject(PredefinedPathwayCourseSelectionService);
    private readonly translateService = inject(TranslateService);

    readonly saving = this.pathwayFacade.saving;

    readonly typeOptions = getTypeOptions(this.translateService);
    readonly schoolOptions = computed(() => this.formOptionsFacade.schools());
    readonly organisationOptions = computed(() => this.formOptionsFacade.organisations());

    readonly selectedCourses = signal<CourseSelection[]>([]);

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        type: this.formBuilder.control<PathwayType>('online', [FormValidators.Required]),
        schoolIds: this.formBuilder.control<string[]>([]),
        organisationIds: this.formBuilder.control<string[]>([]),
    });

    constructor() {
        if (this.formOptionsFacade.schoolsLoaded() !== true) {
            this.formOptionsFacade.getSchools();
        }
        if (this.formOptionsFacade.organisationsLoaded() !== true) {
            this.formOptionsFacade.getOrganisations();
        }

        effect(() => {
            if (this.pathwayFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.pathwayFacade.saving() === false && this.pathwayFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.courseSelectionService.selected();
            if (selection) {
                this.selectedCourses.update((courses) => {
                    if (courses.some((c) => c.id === selection.id)) {
                        return courses;
                    }
                    return [...courses, selection];
                });
                this.courseSelectionService.clear();
            }
        });
    }

    ngOnDestroy(): void {
        this.pathwayFacade.clear();
    }

    openCourseDialog(): void {
        this.router.navigate([{ outlets: { dialog: ['select-predefined-pathway-course'] } }], { relativeTo: this.route.parent?.parent });
    }

    removeCourse(courseId: string): void {
        this.selectedCourses.update((courses) => courses.filter((c) => c.id !== courseId));
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const value = this.form.getRawValue();
            this.pathwayFacade.createPathway({
                name: value.name,
                description: value.description,
                preloaded: true,
                type: value.type,
                schoolIds: value.schoolIds,
                organisationIds: value.organisationIds,
                courseIds: this.selectedCourses().map((c) => c.id),
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }], { relativeTo: this.route.parent?.parent });
    }
}
